/**
 * planFingerprint — "is the plan you are looking at still the answer?"
 *
 * The optimizer is fast (~7ms) and the plan it produced is a snapshot. Change a
 * price, change your budget, lock a player, switch objective, and the plan on
 * screen is a stale answer to a question you no longer asked — with nothing on
 * screen saying so. This is what says so. The Rebuild button reads it.
 *
 * 🔴 A HASH OF THE INPUTS, NOT A TIMESTAMP. "Optimized 4 minutes ago" answers a
 * question nobody has. The useful question is whether anything the solve
 * DEPENDED ON has moved, and only a content hash can answer that — it also
 * means undoing a change (raise a price, put it back) correctly returns the
 * button to quiet rather than leaving a permanent "something happened" mark.
 *
 * WHAT IT COVERS, and each is a real solver input:
 *   • the objective            — what is being maximised
 *   • the budget mode          — total pot, or a cap per spot
 *   • the plan budget          — the knapsack capacity
 *   • every per-slot earmark   — capacity taken out before the solve
 *   • the roster shape         — the slots being filled and what may fill them
 *   • every target's PRICE     — see below; one rule, three behaviours
 *   • your price-sheet entries — priceOf reads these first (see makePriceOf)
 *   • your PPG-sheet entries   — both objectives honour these
 *   • the set of locks         — WHICH slots are held, not just what they cost
 *
 * 🔴 THE PRICES COME OFF THE TARGETS, NOT OFF THE SHEET AND NOT OFF THE BOARD.
 * `target.price` is already maintained as the EFFECTIVE price by the two
 * writers in usePlanState, so hashing that one field carries all three cases
 * with no branching here and — more importantly — with no second definition of
 * "what this player costs" that could drift from the solver's:
 *
 *   locked  → resyncMarket and applySheetToPlans both refuse to touch it, so it
 *             contributes the LOCKED price. The board moving under a player you
 *             have already bought cannot change the solve, and must not raise
 *             the warning. The dials get moved constantly; a warning that fires
 *             when nothing changed is one nobody reads by the second draft.
 *   edited  → applySheetToPlans materialises your override onto it, so it
 *             contributes your number.
 *   neither → resyncMarket tracks the board onto it, so it contributes the
 *             board price — and a dial move that genuinely changes what the
 *             solver would pick DOES flip the button.
 *
 * 🔴 THE SHEETS ARE STILL HASHED SEPARATELY, and that is not redundant: an
 * override typed for a player who is not placed anywhere yet has no target to
 * ride on, and it is exactly the kind of edit that changes the next solve
 * ("price CMC at $30 and watch the optimizer go and get him").
 *
 * 🔴 THE SHEET ENTRIES ARE INTERSECTED WITH THE POOL, deliberately. A price set
 * for a kicker in a league with no kicker slot cannot change any solve, so it
 * must not be able to raise the warning. Without the intersection every stray
 * entry in a league-wide sheet is a false positive on every plan in that league.
 *
 * 🔴 LOCKS KEEP THEIR OWN COMPONENT even though their price now arrives via the
 * targets. WHICH slots are held is a solve input in its own right — unlocking a
 * player does not change his price (a lock is a statement about money, and
 * taking it back does not un-say it), but it hands his slot and his budget back
 * to the solver, which is a different question to answer.
 */

import { primaryOf, planObjective, planBudgetMode, isLockedTarget } from "../hooks/usePlanState";
import { hashString } from "./hashString.js";

// The hash itself lives in its own module now: the saved-room store keys on the
// same function, and one definition is the only way two identities stay one.
export { hashString };


/** Sorted `k=v` pairs, so object key order can never move the hash. */
const pairs = (entries) => entries
  .map(([k, v]) => `${k}=${v}`)
  .sort()
  .join(",");

/**
 * The canonical string, exported so a failing test can print the two sides and
 * show WHICH input moved rather than two hex strings that differ.
 */
export function fingerprintParts({ plan, rosterSlots, poolKeys, priceSheet, ppgSheet }) {
  const buckets = (plan && plan.buckets) || {};
  const slots = rosterSlots || [];
  const inPool = (key) => !poolKeys || poolKeys.has(key);

  const earmarks = [];
  const locks = [];
  const targets = [];
  slots.forEach((slot) => {
    const bucket = buckets[slot.id];
    if (bucket && bucket.budget != null) earmarks.push([slot.id, Math.max(0, Number(bucket.budget) || 0)]);
    // EVERY cell, not only the primary. The backups are whole alternative
    // rosters (see primaryOf), so the board moving under one of them moves a
    // number this plan is built on — and it is the same one rule either way.
    ((bucket && bucket.cells) || []).forEach((cell, cellIdx) => {
      if (!cell) return;
      targets.push([`${slot.id}#${cellIdx}`, `${cell.key}@${Number(cell.price) || 0}`]);
    });
    const primary = primaryOf(bucket);
    // The SET of held slots. Its price already rode in on the target above.
    if (isLockedTarget(primary)) locks.push([slot.id, primary.key]);
  });

  const sheetPairs = (sheet) => pairs(
    Object.entries(sheet || {}).filter(([key]) => inPool(key))
  );

  return [
    `obj:${planObjective(plan)}`,
    `mode:${planBudgetMode(plan)}`,
    `budget:${Math.max(0, Number(plan && plan.budget) || 0)}`,
    `slots:${slots.map((s) => `${s.id}:${s.pos}`).join("|")}`,
    `earmarks:${pairs(earmarks)}`,
    `targets:${pairs(targets)}`,
    `locks:${pairs(locks)}`,
    `prices:${sheetPairs(priceSheet)}`,
    `ppg:${sheetPairs(ppgSheet)}`,
  ].join(";");
}

/** The stored/compared value. */
export function planFingerprint(args) {
  return hashString(fingerprintParts(args));
}

export default planFingerprint;
